/**
 * useIntranetSites — fetches published CM sites for the left-nav.
 *
 * Sprint CM-5b. Sites are surfaced under an "Intranet Sites" group
 * between the MAIN and MODULES nav sections. We poll once on mount
 * and re-fetch when the auth context changes (different tenant or
 * user) so site visibility tracks RBAC.
 *
 * Failure mode is silent — sidebar still renders; sites group just
 * stays empty if the API fails.
 *
 * Cross-References:
 *   - Sprint CM-5b
 *   - assessment-cm-sites-restructure.md §"Left nav: Intranet Sites"
 */
import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';

export interface IntranetSite {
  id: string;
  name: string;
  slug: string;
  icon: string | null;
  is_published: boolean;
  landing_page_id: string | null;
  external_url: string | null;
  /** Sprint CM-15 TKT-2 — resolved external-site favicon URL. */
  external_favicon_url?: string | null;
  /** Sprint CM-15 TKT-4 — user-controlled sidebar order. */
  sort_order?: number;
}

interface ApiSite extends IntranetSite {
  description: string | null;
  theme: Record<string, unknown>;
  // Sprint CM-AUD-8 — audience concept retired entirely; no audience
  // field on the response. Per-page visibility is in the grants table.
  landing_page_id: string | null;
  external_url: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

/**
 * Last roots fetched, per user. Each route group mounts its own Layout,
 * so the sidebar remounts; starting from this keeps the CM section in
 * place instead of popping in and shifting the nav below it.
 */
let cached: { key: string; sites: IntranetSite[] } | null = null;

export function useIntranetSites(): {
  sites: IntranetSite[];
  loading: boolean;
} {
  const userId = useAuthStore((s) => s.user?.id);
  const tenantId = useAuthStore((s) => s.user?.tenantId);
  const cacheKey = userId && tenantId ? `${tenantId}:${userId}` : null;
  const [sites, setSites] = useState<IntranetSite[]>(
    () => (cacheKey && cached?.key === cacheKey ? cached.sites : []),
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId || !tenantId) {
      setSites([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    // CM-17 — Pages-as-Roots: sidebar now reads root pages, not Sites.
    // The /pages/roots response shape mirrors the legacy /sites shape,
    // so the rest of this hook is unchanged.
    apiFetch<ApiSite[]>('/mod/cm/pages/roots?published_only=true')
      .then((data) => {
        if (cancelled) return;
        const next = (data ?? []).map((s) => ({
          id: s.id,
          name: s.name,
          slug: s.slug,
          icon: s.icon,
          is_published: s.is_published,
          landing_page_id: s.landing_page_id,
          external_url: s.external_url ?? null,
          external_favicon_url: s.external_favicon_url ?? null,
          sort_order: s.sort_order ?? 0,
        }));
        cached = { key: `${tenantId}:${userId}`, sites: next };
        setSites(next);
      })
      .catch((err) => {
        if (cancelled) return;
        cached = null;
        // Sites flag off → 404. Stay empty silently.
        if (err instanceof ApiError && err.status === 404) {
          setSites([]);
          return;
        }
        // Other errors — also silent; sidebar shouldn't block on
        // a CM hiccup.
        setSites([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId, tenantId]);

  return { sites, loading };
}
